import {LineItemUser, Modifier, Money, OrderStatus, OrderStatusUser} from '../models/order.model';
import { CartItem } from '../models/cart-item.model';
import { Coupon, Price, ProductModifier, ProductVariation, RestaurantAddress } from '../models/restaurant.model';
import { CurrentOrder, CurrentOrderLocation, LiveOrders } from '../models/live-order.model';
import { MONTH } from '../enums/util.enum';
import {parseDuration} from './restaurant.dto';

export const dataToOrdersStatus = (order: any): OrderStatus => {
  return new OrderStatus(
    order.order_id,
    order.order_number,
    order.status,
    order.square_customer_id,
    order.datetime_created,
    order.fulfillment_type,
    order.is_asap,
    order.pickup_at,
    order?.line_items.map(item => dataToCartItem(item)),
    order.location_id,
    order.location_name,
    order.square_receipt_url
  );
};

export const dataToOrdersStatuses = (data: any): OrderStatus[] => {
  return data.content.map(orders => {
    return dataToOrdersStatus(orders);
  });
};

export const dataToCartItem = (data: any): CartItem => {
  return new CartItem(
    data.name,
    new ProductVariation(
      data.catalog_object_id,
      data.variation_name,
      new Price(
        data.variation_total_price_money.amount,
        data.variation_total_price_money.currency
      )
    ),
    modifiers(data.modifiers),
    stringToNumber(data.quantity),
    data.note,
    new Price(
      data.total_money.amount,
      data.total_money.currency
    )
  );
};

export function stringToNumber(data: any): number {
  const parsed = parseInt(data, 10);
  if (isNaN(parsed)) {
    return data;
  }
  return parsed;
}

export function modifiers(data: any): [] {
  if (!data) {
    return [];
  }
  return data.map(
    (modifier) => new ProductModifier(
      modifier.catalog_object_id,
      modifier.name,
      new Price(
        modifier.total_price_money.amount,
        modifier.total_price_money.currency
      )
    )
  );
}

export const dataToLiveOrders = (data: any): LiveOrders => {
  return new LiveOrders(
    data.RESERVED ? dataToCurrentOrders(data.RESERVED) : [],
    data.PREPARED ? dataToCurrentOrders(data.PREPARED) : []
  );
};

export const dataToCurrentOrder = (data: any): CurrentOrder => {
  return new CurrentOrder(
    data.id,
    data.source,
    data.number,
    data.type,
    data.state,
    data.customer_name,
    convertToUSDate(data.pickup_at),
    data?.location_name,
    convertToDate(data.pickup_at)
  );
};

export const dataToCurrentOrders = (data: any[]): CurrentOrder[] => {
  return data.map(order => dataToCurrentOrder(order));
};

export function convertToUSDate(data: string): string {
  const newDate = new Date(data);
  let hours = newDate.getHours();
  let minutes: string | number = newDate.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return hours + ':' + minutes + ' ' + ampm;
}

export function convertToDate(data: string): string {
  const newDate = new Date(data);
  return `${newDate.getMonth() + 1}/${newDate.getDate()}`;
}

export const dataToOrderShop = (data: any): CurrentOrderLocation => {
  return new CurrentOrderLocation(
    data.venue.kyoo_location.display_name,
    data.venue.kyoo_location.contact_phone,
    new RestaurantAddress (
      data.venue.address.country,
      data.venue.address.address_line1,
      data.venue.address.locality,
      data.venue.address.administrative_district_level1,
      data.venue.address.postal_code,
    ),
    data.venue.kyoo_location.logo_image_url
  );
};

export const dataToDiscount = (data: any): Coupon => {
  return new Coupon(
    data.code,
    data.id,
    data.type
  );
};

export const dataToCartItems = (data: Array<any>): Array<CartItem> => {
  return data?.map((item: any) =>
    new CartItem(
      item.name ?? '',
      new ProductVariation(
        item.catalog_object_id,  // TODO: change to variation_id
        item.variation_name ?? '',
        new Price(
          item.variation_total_price_money?.amount ?? 0,
          item.variation_total_price_money?.currency ?? ''
        )
      ),
      item.modifiers?.map((modifier: any) =>
        new ProductModifier(
          modifier.uid ?? '',
          modifier.name ?? '',
          new Price(
            modifier.total_price_money?.amount ?? 0,
            modifier.total_price_money?.currency ?? ''
          ),
          true,
          false,
          modifier?.hidden_from_customer
        )
      ),
      item.quantity ?? 0,
      item.special_instructions ?? '',
      new Price(item.total_money?.amount ?? 0, item.total_money?.currency ?? '')
    )
  ) || [];
};

export const transformOrder = (order: any): OrderStatusUser => {
  return new OrderStatusUser(
    order.order_number,
    order.status,
    order.datetime_created,
    order.fulfillment_type,
    order.pickup_at,
    parseDuration(order.prep_time_duration),
    order.line_items.map(item => new LineItemUser(
      item.name,
      item.variation_name,
      item.quantity,
      item.modifiers ? item.modifiers.map(mod => new Modifier(mod.name)) : [],
      new Money(item.total_price_money.amount, item.total_price_money.currency)
    )),
    new Money(order.total_discount_money?.amount ?? 0, order.total_discount_money?.currency ?? 'USD'),
    new Money(order.total_tax_money?.amount ?? 0, order.total_tax_money?.currency ?? 'USD'),
    new Money(order.total_delivery_fee_money?.amount ?? 0, order.total_delivery_fee_money?.currency ?? 'USD'),
    new Money(order.total_tip_money?.amount ?? 0, order.total_tip_money?.currency ?? 'USD'),
    new Money(order.total_driver_tip_money?.amount ?? 0, order.total_driver_tip_money?.currency ?? 'USD'),
    new Money(order.total_money?.amount ?? 0, order.total_money?.currency ?? 'USD'),
    order.is_asap
  );
};

